import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { Form, FormText } from 'react-bootstrap';
import { Cloudinary } from '@cloudinary/url-gen';
import { DragDropContext, Droppable, Draggable } from 'react-beautiful-dnd';
import { toInteger, isEqual } from 'lodash';
import { eachOfSeries } from 'async';
import { Media, UnsavedMedia } from '../../../../structures/models/media/media';
import { ExternalMedia, UnsavedExternalMedia } from '../../../../structures/models/media/external';
import {
  CloudinaryUploadPreset,
  CLOUDINARY_CLOUD_NAME,
  CLOUDINARY_UPLOAD_PRESET_LISTING,
  CLOUDINARY_UPLOAD_PRESET_DEFAULT,
  CLOUDINARY_UPLOAD_SOURCES,
} from '../../../../utils/constants';
import { upload as CloudinaryUploadService } from '../../../../utils/ajax/cloudinaryAjax';
import {
  deleteMedia as deleteMediaAjax,
  updateMedia,
} from '../../../../utils/ajax/mediaAjax';
import { CreateMediaRequest } from '../../../../structures/requests/media';
import {
  AllCloudinaryMedia,
  AllExternalMedia,
  AllMedia,
  combineAndSortImages,
  getThumbnailUrl,
} from './utils';

// @ts-ignore
const { cloudinary } = window;

type LabelOptions = {
  show?: boolean;
  text?: string;
  class?: string;
}

type Options = {
  labelOptions?: LabelOptions;
};

interface CloudinaryWidgetParams {
  uploadPreset?: CloudinaryUploadPreset,
}

interface CloudinaryUploadFieldParams {
  shouldUpload: boolean,
  autoUpdateOnSort?: boolean,
  autoDeleteOnRemove?: boolean,
  shouldAllowImages: boolean,
  shouldAllowVideos: boolean,
  shouldAllowExternalMedia: boolean,
  uploadImageFunction?: (
    val: CreateMediaRequest,
  ) => Promise<Media | string>,
  uploadVideoFunction?: (
    val: CreateMediaRequest,
  ) => Promise<Media | string>,
  uploadExternalMediaFunction?:
  (
    val: UnsavedExternalMedia,
  ) => Promise<ExternalMedia | string>,
  initialVideoMedia?: Array<AllCloudinaryMedia>,
  initialImageMedia?: Array<AllCloudinaryMedia>,
  initialExternalVideoMedia?: Array<AllExternalMedia>,
  updateStart?: () => void,
  updateComplete?: () => void,
  onSortEnd?: () => void,
  maxMediaCount?: number,
  uploadPreset?: string,
  label?: string,
  labelClass?: string,
  options?: Options, // Style related options for this component
  cloudinaryWidgetParams?: CloudinaryWidgetParams
  onMediaChange?: (allMedia: AllMedia[]) => void,
}

type SortableMedia = {
  id: string,
  media: AllMedia
}

type CloudinaryEditorType = {
  destroy?: (public_id: string) => void;
  update?: (params: object) => void;
  show?: () => void;
  on?: (action: string, callback: (result: any) => any) => void;
}

const cloudinaryMedia = new Cloudinary({
  cloud: {
    cloudName: CLOUDINARY_CLOUD_NAME,
  },
});

// TODO: when/if needed, add functionality for external media as we do for media
const CloudinaryUploadField: React.FC<CloudinaryUploadFieldParams> = (props): ReactElement => {
  const {
    shouldUpload,
    shouldAllowImages,
    shouldAllowVideos,
    uploadImageFunction = false,
    uploadVideoFunction = false,
    initialVideoMedia = [],
    initialImageMedia = [],
    initialExternalVideoMedia = [],
    maxMediaCount = null,
    updateComplete = () => { },
    onSortEnd = () => { },
    updateStart = () => { },
    options = {},
    onMediaChange = () => { },
    label,
    labelClass = '',
    cloudinaryWidgetParams,
    autoUpdateOnSort = true,
    autoDeleteOnRemove = true,
  } = props;
  const [allMedia, setAllMedia] = useState<Array<AllMedia>>(
    combineAndSortImages(initialVideoMedia, initialImageMedia, initialExternalVideoMedia),
  );
  const [mediaTouched, setMediaTouched] = useState(false);
  const [cloudinaryEditor, setCloudinaryEditor] = useState<CloudinaryEditorType>();
  const sortMediaList: Array<SortableMedia> = [];
  // eslint-disable-next-line guard-for-in,no-restricted-syntax
  for (const index in allMedia) {
    sortMediaList.push({
      id: JSON.stringify(allMedia[index]),
      media: allMedia[index],
    });
  }

  useEffect(() => {
    if (onMediaChange) {
      if (!isEqual(initialImageMedia, allMedia)) {
        if (!mediaTouched) {
          setMediaTouched(true);
        } else {
          onMediaChange(allMedia);
        }
      } else if (mediaTouched) {
        onMediaChange(allMedia);
      }
    }
  }, [allMedia, mediaTouched, onMediaChange, initialImageMedia]);

  useEffect(() => {
    if (cloudinary) {
      setCloudinaryEditor(cloudinary.mediaEditor());
    }
  }, []);

  const labelOptions: LabelOptions = {
    show: true,
    text: 'Photos',
    class: '',
    ...options?.labelOptions,
  };

  const cloudinaryWidgetOptions: CloudinaryWidgetParams = {
    uploadPreset: CLOUDINARY_UPLOAD_PRESET_DEFAULT,
    ...cloudinaryWidgetParams,
  };

  const [uploadSuccess, setUploadSuccess] = useState<boolean | null>(null);

  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [dragging, setDragging] = useState(false);
  const isAtMaxCount = (
    (maxMediaCount && allMedia.length - maxMediaCount >= 0) || allMedia.length >= 30
  );
  let statusColor = 'text-primary';
  if (uploadSuccess != null) {
    statusColor = uploadSuccess ? 'text-success' : 'text-danger';
  }

  const setMessageAndStatus = (status: boolean, statusMessage: string) => {
    setMessage(statusMessage);
    setUploadSuccess(status);
  };

  const upload = useCallback(async () => {
    if (!shouldUpload) {
      return;
    }
    updateStart();
    setLoading(true);
    let newMediaObj = [...allMedia];
    let changed = false;

    if (!autoDeleteOnRemove) {
      eachOfSeries(
        initialImageMedia as AllCloudinaryMedia[], (async (image, index, callback) => {
          const stillExists = (allMedia as AllCloudinaryMedia[])
            .find((media) => (media as Media).id === (image as Media).id);
          if (!stillExists) {
            await deleteMediaAjax((image as Media).id);
          }
          callback();
        }),
      ).then(() => {
        eachOfSeries(newMediaObj as AllCloudinaryMedia[], (async (media, index, callback) => {
          if ((media as unknown as UnsavedMedia).file_type) {
            if ((media as unknown as UnsavedMedia).file_type === 'image' && uploadImageFunction && !(media as unknown as Media).id) {
              changed = true;
              const returnedMedia: Media | string = await uploadImageFunction(
                (media as unknown as CreateMediaRequest),
              );
              if ((returnedMedia as Media).id) {
                newMediaObj[index] = (returnedMedia as Media);
              } else {
                setUploadSuccess(false);
                setMessage(returnedMedia as string);
                newMediaObj = newMediaObj.splice(index, 1);
              }
            } else if ((media as unknown as UnsavedMedia).file_type === 'video' && uploadVideoFunction && !(media as unknown as Media).id) {
              changed = true;
              const returnedMedia: Media | string = await uploadVideoFunction(
                (media as unknown as CreateMediaRequest),
              );
              if ((returnedMedia as Media).id) {
                newMediaObj[index] = (returnedMedia as Media);
              } else {
                setUploadSuccess(false);
                setMessage(returnedMedia as string);
                newMediaObj = newMediaObj.splice(index, 1);
                // TODO add external upload capability.
              }
            } else if ((media as Media).id && !autoUpdateOnSort) {
              const initialMediaObject = (initialImageMedia as AllCloudinaryMedia[])
                .find((image) => (image as Media).id === (media as Media).id);
              if (initialMediaObject && initialMediaObject.sort_id !== index) {
                await updateMedia(toInteger((media as Media).id), { sort_id: index });
              }
            }
            callback();
          }
        }))
          .then(() => {
            setLoading(false);
            updateComplete();
            if (changed) {
              setAllMedia(newMediaObj);
            }
          });
      });
    } else {
      eachOfSeries(newMediaObj as AllCloudinaryMedia[], (async (media, index, callback) => {
        if ((media as unknown as UnsavedMedia).file_type) {
          if ((media as unknown as UnsavedMedia).file_type === 'image' && uploadImageFunction && !(media as unknown as Media).id) {
            changed = true;
            const returnedMedia: Media | string = await uploadImageFunction(
              (media as unknown as CreateMediaRequest),
            );
            if ((returnedMedia as Media).id) {
              newMediaObj[index] = (returnedMedia as Media);
            } else {
              setUploadSuccess(false);
              setMessage(returnedMedia as string);
              newMediaObj = newMediaObj.splice(index, 1);
            }
          } else if ((media as unknown as UnsavedMedia).file_type === 'video' && uploadVideoFunction && !(media as unknown as Media).id) {
            changed = true;
            const returnedMedia: Media | string = await uploadVideoFunction(
              (media as unknown as CreateMediaRequest),
            );
            if ((returnedMedia as Media).id) {
              newMediaObj[index] = (returnedMedia as Media);
            } else {
              setUploadSuccess(false);
              setMessage(returnedMedia as string);
              newMediaObj = newMediaObj.splice(index, 1);
              // TODO add external upload capability.
            }
          } else if ((media as Media).id && !autoUpdateOnSort) {
            const initialMediaObject = (initialImageMedia as AllCloudinaryMedia[])
              .find((image) => (image as Media).id === (media as Media).id);
            if (initialMediaObject && initialMediaObject.sort_id !== index) {
              await updateMedia(toInteger((media as Media).id), { sort_id: index });
            }
          }
          callback();
        }
      }))
        .then(() => {
          setLoading(false);
          updateComplete();
          if (changed) {
            setAllMedia(newMediaObj);
          }
        });
    }
  }, [
    allMedia,
    initialImageMedia,
    shouldUpload,
    autoDeleteOnRemove,
    autoUpdateOnSort,
    setUploadSuccess,
    setMessage,
    uploadImageFunction,
    uploadVideoFunction,
    updateStart,
    updateComplete,
    updateMedia,
    deleteMediaAjax,
    setAllMedia,
    setLoading,
  ]);

  useEffect(() => {
    upload();
  }, [shouldUpload]);

  const clientAllowedFormats: Array<string> = [];
  if (shouldAllowImages) clientAllowedFormats.push('image');
  if (shouldAllowVideos) clientAllowedFormats.push('video');

  // Create cloudinary upload widget instance
  // TODO: when needed, make a customizable widgetOptions component prop to override defaults

  const widgetOptions = {
    cloudName: CLOUDINARY_CLOUD_NAME,
    uploadPreset: cloudinaryWidgetParams?.uploadPreset
      ? cloudinaryWidgetParams?.uploadPreset
      : CLOUDINARY_UPLOAD_PRESET_LISTING,
    multiple: (maxMediaCount && maxMediaCount > 1),
    maxFiles: (maxMediaCount) ? maxMediaCount - allMedia.length : 30,
    clientAllowedFormats,
    sources: CLOUDINARY_UPLOAD_SOURCES,
    showPoweredBy: false,
    text: {
      en: {
        crop: {
          title: 'Set image focus',
          crop_btn: 'Save',
        },
      },
    },
  };
  const widgetFunction = (error, result) => {
    if (result.event === 'abort') {
      upload();
    }
    if (!error && result && result.event === 'success') {
      const newImage = result.info;
      const formattedNewFile = {
        file_url: newImage.secure_url,
        public_id: newImage.public_id,
        file_name: newImage.original_filename,
        file_type: newImage.resource_type,
        sort_id: allMedia.length + 1,
        size: newImage.bytes,
      };
      if (newImage.resource_type === 'image') {
        allMedia.push(formattedNewFile);
        setAllMedia([...allMedia]);
      }
      if (newImage.resource_type === 'video') {
        allMedia.push(formattedNewFile);
        setAllMedia([...allMedia]);
      }
    }
  };

  const deleteMedia = useCallback(async (index: number): Promise<void> => {
    setLoading(true);
    try {
      const mediaElement = allMedia[index];
      if ((mediaElement as unknown as Media).id && autoDeleteOnRemove) {
        await deleteMediaAjax((mediaElement as unknown as Media).id);
      }

      allMedia.splice(index, 1);
      setAllMedia([...allMedia]);
    } catch (e) {
      setLoading(false);
    }
    setLoading(false);
  }, [autoDeleteOnRemove]);

  const editMedia = async (public_id: string, index: number): Promise<void> => {
    if (typeof cloudinaryEditor === 'undefined') { return; }
    setLoading(true);
    try {
      cloudinaryEditor.update?.({
        publicIds: [public_id],
        cloudName: CLOUDINARY_CLOUD_NAME,
        image: {
          steps: ['resizeAndCrop'],
        },
      });

      cloudinaryEditor.on?.('export', async (result) => {
        const { secureUrl: file_url } = result.assets[0];
        const preset = cloudinaryWidgetOptions.uploadPreset
          ? cloudinaryWidgetOptions.uploadPreset : CLOUDINARY_UPLOAD_PRESET_DEFAULT;

        const UnsavedMediaFile = await CloudinaryUploadService(file_url, index, preset);
        allMedia[index] = UnsavedMediaFile;

        setAllMedia([...allMedia]);
      });

      cloudinaryEditor.on?.('close', async (result) => {
        // @ts-ignore
        cloudinaryEditor.destroy();
        /**
         * cleanup after destroying media editor
         */
        document.body.style.overflowY = 'auto';
      });

      cloudinaryEditor.show?.();
    } catch (e) {
      setLoading(false);
    }
    setLoading(false);
  };

  /**
   * when an image drag stops,
   * we consume the 'results' and re-sort
   * @param resultObj
   */
  const onDragEnd = useCallback(async (resultObj: object) => {
    // @ts-ignore
    if (!resultObj.destination) return;

    setLoading(true);
    const result = [...allMedia];
    // @ts-ignore
    const sourceIndex = resultObj.source.index;

    // Handle dragging to invalid location & fail gracefully
    // @ts-ignore
    if (resultObj.destination === null) {
      setLoading(false);
      return;
    }

    // @ts-ignore
    const destinationIndex = resultObj.destination.index;
    // 'removed' refers to the media entity we're now splicing into a new
    // index into our 'result' array
    const [removed] = result.splice(sourceIndex, 1);
    // @ts-ignore
    result.splice(destinationIndex, 0, removed);
    // eslint-disable-next-line no-plusplus,for-direction
    for (let index = 0; index < result.length; index++) {
      // perform 'updates' on all media once there is a drag end, we do this because
      // we have to reset the sort-order of media on drag-end.
      const oneMedia = result[index];
      if ((oneMedia as Media).id && oneMedia.sort_id !== index) {
        if (autoUpdateOnSort) {
          // eslint-disable-next-line no-await-in-loop
          await updateMedia(toInteger((oneMedia as Media).id), { sort_id: index });
        }
      }
      result[index].sort_id = index;
    }

    if (!isEqual(result, allMedia)) {
      setAllMedia([...result]);
    }
    setLoading(false);
    if (onSortEnd) {
      onSortEnd();
    }
  }, [allMedia, onSortEnd, autoUpdateOnSort]);

  return (
    <Form.Group className="dropzone-form">
      {label && (
        <Form.Label className={labelClass}>
          {label}
        </Form.Label>
      )}
      {!!(!label && labelOptions.show) && (
        <Form.Label className={labelOptions.class}>
          {labelOptions.text}
        </Form.Label>
      )}
      <FormText className={statusColor}>
        {loading ? (<i className="fa fa-spinner fa-spin" />) : <></>}
        {' '}
        {message}
      </FormText>
      <div
        id="photo-upload-gallery"
        className="d-flex flex-nowrap col-md-12 bg-white p-3 border rounded photo-upload-gallery"
      >
        <section>
          <DragDropContext
            onDragStart={() => {
              setDragging(true);
            }}
            onDragEnd={(result) => {
              setDragging(false);
              if (!loading) {
                onDragEnd(result);
              }
            }}
          >
            <Droppable
              droppableId="droppable"
              direction="horizontal"
            >
              {(provided, snapshot) => (
                <div
                  className="wrapper d-flex"
                  ref={provided.innerRef}
                  {...provided.droppableProps}
                >
                  {sortMediaList.map((listItem, index) => {
                    const mediaItem = listItem.media;
                    const filename = (mediaItem as unknown as AllCloudinaryMedia).file_name
                      || (mediaItem as ExternalMedia).resource_link;
                    return (
                      <Draggable
                        key={`${listItem.id + 1}`}
                        draggableId={`${listItem.id + 1}`}
                        index={index}
                      >
                        {
                          (draggableProvided) => (
                            <div
                              ref={draggableProvided.innerRef}
                              {...draggableProvided.draggableProps}
                              {...draggableProvided.dragHandleProps}
                              className="photo-drag"
                              key={listItem.id}
                            >
                              <div
                                key={`div-${listItem.id}`}
                                className="m-2 photo-uploaded"
                              >
                                <div style={{ position: 'relative' }}>
                                  <span
                                    className="position-absolute text-white top-0 end-0 z-10"
                                  >
                                    <button
                                      aria-label={`delete ${filename}`}
                                      type="button"
                                      style={{ all: 'unset' }}
                                      onClick={async () => {
                                        if (!loading) {
                                          await deleteMedia(index);
                                        }
                                      }}
                                    >
                                      <i className={loading ? 'hidden' : 'fas fa-times-circle'} />
                                    </button>
                                  </span>
                                  {/* eslint-disable-next-line max-len */}
                                  {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events */}
                                  <div
                                    className="uploaded-thumbnail"
                                    onClick={async () => {
                                      // we can only edit assets that are uploaded to cloudinary
                                      if (!loading && typeof (mediaItem as unknown as Media).public_id !== 'undefined') {
                                        // eslint-disable-next-line max-len
                                        await editMedia((mediaItem as unknown as Media).public_id, index);
                                      }
                                    }}
                                  >
                                    <div
                                      className="layered-thumbnail"
                                      style={{
                                        backgroundImage: `url('${getThumbnailUrl(mediaItem, cloudinaryMedia)}')`,
                                      }}
                                    >
                                      <div
                                        style={{
                                          backgroundImage: `url('${getThumbnailUrl(mediaItem, cloudinaryMedia)}')`,
                                        }}
                                      />
                                    </div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          )
                        }
                      </Draggable>
                    );
                  })}
                  <div className={loading || dragging ? 'hidden photo-drag' : 'photo-drag'}>
                    <div className="m-2 photo-uploaded">
                      <button
                        type="button"
                        style={{ all: 'unset' }}
                        className={(loading || isAtMaxCount) ? 'hidden' : ''}
                        aria-label="add a new image"
                        disabled={
                          !!((isAtMaxCount))
                        }
                        onClick={() => {
                          if (
                            isAtMaxCount) {
                            return;
                          }
                          const localCloudinaryUploadWidget = cloudinary.createUploadWidget(
                            widgetOptions,
                            widgetFunction,
                          );
                          localCloudinaryUploadWidget.open();
                        }}
                      >
                        <div className="file-upload upload_widget upload_widget_images my-auto">
                          <i
                            className="fas fa-plus pe-none"
                          />
                        </div>
                      </button>
                    </div>
                  </div>
                  {provided.placeholder}
                </div>
              )}
            </Droppable>
          </DragDropContext>
        </section>
      </div>
    </Form.Group>
  );
};
export default CloudinaryUploadField;
